/**
 * The answer key of an akari puzzle is the set of bulbs. Kept apart from the form component
 * so the eagerly loaded module file never pulls the author's board into the player bundle.
 */
import { blankRows, clearBlacks, resizeRows } from "./progress";
import { BULB, EMPTY, type AkariDefinition, type AkariSolution } from "./types";

/**
 * What the core hands the form when nothing is queued: no rows at all, so the form can tell
 * "nothing typed" from a deliberately empty board. `bulbsOf` pads it to a blank grid.
 */
export function emptySolution(_definition: AkariDefinition): AkariSolution {
  return { rows: [] };
}

/** Whether a stored value is a bulb grid at all, whatever its size. */
export function isBulbGrid(value: unknown): value is AkariSolution {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const rows = (value as { rows?: unknown }).rows;
  return Array.isArray(rows) && rows.every((row) => typeof row === "string" && /^[*.]*$/.test(row));
}

/** Nothing queued: not a bulb grid, or the empty one the core hands out. */
export function isAbsent(value: unknown): boolean {
  return !isBulbGrid(value) || value.rows.length === 0;
}

/** Reads whatever is stored, however wrong, as a bulb grid of the definition's size. */
export function bulbsOf(value: unknown, definition: AkariDefinition): string[] {
  if (!isBulbGrid(value)) return blankRows(definition.rows, definition.cols);
  return clearBlacks(
    resizeRows(
      value.rows.map((row) => Array.from(row, (c) => (c === BULB ? BULB : EMPTY)).join("")),
      definition.rows,
      definition.cols,
    ),
    definition.grid,
  );
}
